import React from 'react';
import { Snowflake, Droplets, Wind, Flame } from 'lucide-react';

export interface ThermostatModeStyle01Props {
  value?: string;
  options?: string[] | string;  // Accept both array and comma-separated string
  disabled?: boolean;
  loading?: boolean;
  onChange: (value: string) => void;
  label?: string;
}

// HVAC mode configurations
const MODE_CONFIG: Record<string, { 
  icon: React.ElementType; 
  label: string; 
  activeColor: string;
  activeBg: string;
}> = {
  'cool': { 
    icon: Snowflake, 
    label: '冷氣', 
    activeColor: 'text-cyan-400',
    activeBg: 'bg-cyan-500'
  },
  'dry': { 
    icon: Droplets, 
    label: '除濕', 
    activeColor: 'text-blue-400',
    activeBg: 'bg-blue-500'
  },
  'fan-only': { 
    icon: Wind, 
    label: '送風', 
    activeColor: 'text-teal-400',
    activeBg: 'bg-teal-500'
  },
  'heat': { 
    icon: Flame, 
    label: '暖氣', 
    activeColor: 'text-orange-400',
    activeBg: 'bg-orange-500'
  },
  'auto': { 
    icon: Snowflake, 
    label: '自動', 
    activeColor: 'text-purple-400',
    activeBg: 'bg-purple-500'
  },
};

const DEFAULT_MODES = ['cool', 'dry', 'fan-only', 'heat'];

/**
 * ThermostatMode Style 01 - Icon Buttons
 * HVAC mode selection with icons and labels
 * Command: ThermostatSetMode
 */
export const Style01_IconButtons: React.FC<ThermostatModeStyle01Props> = ({
  value,
  options: rawOptions = DEFAULT_MODES,
  disabled = false,
  loading = false,
  onChange,
  label = '模式'
}) => {
  // Ensure options is always an array (handle string input)
  const options = Array.isArray(rawOptions) 
    ? rawOptions 
    : (typeof rawOptions === 'string' ? rawOptions.split(',').map(s => s.trim()) : DEFAULT_MODES);

  const getModeConfig = (mode: string) => {
    return MODE_CONFIG[mode] || { 
      icon: Wind, 
      label: mode, 
      activeColor: 'text-slate-400',
      activeBg: 'bg-slate-500'
    };
  };

  return (
    <div className="space-y-3">
      <span className="text-sm font-medium text-slate-300">{label}</span>
      <div className="flex gap-2">
        {options.map((mode) => {
          const isSelected = value === mode;
          const config = getModeConfig(mode);
          const Icon = config.icon;
          
          return (
            <button
              key={mode}
              onClick={() => onChange(mode)}
              disabled={disabled || loading}
              className={`flex-1 flex flex-col items-center gap-2 py-3 px-2 rounded-2xl transition-all ${
                isSelected
                  ? `${config.activeBg} text-white shadow-lg`
                  : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isSelected ? 'bg-white/20' : 'bg-slate-700/50'
              }`}>
                <Icon className="w-5 h-5" />
              </div>
              <span className="text-xs font-medium">
                {loading && isSelected ? '...' : config.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Style01_IconButtons;
